import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useCart } from "@/context/CartContext";
import { API } from "@/lib/constants";

// Simple phone formatter
const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) return '';
  if (digits.length <= 1) return `+${digits}`;
  if (digits.length <= 4) return `+${digits.slice(0,1)} (${digits.slice(1)}`;
  if (digits.length <= 7) return `+${digits.slice(0,1)} (${digits.slice(1,4)}) ${digits.slice(4)}`;
  if (digits.length <= 9) return `+${digits.slice(0,1)} (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  return `+${digits.slice(0,1)} (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7,9)}-${digits.slice(9,11)}`;
};

const CheckoutForm = ({ onClose }) => {
  const { cartItems, total, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [deliveryType, setDeliveryType] = useState('pickup');
  const [formData, setFormData] = useState({ 
    customer_name: '', 
    customer_phone: '', 
    customer_email: '',
    delivery_address: '', 
    comment: '' 
  });

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setFormData(p => ({ ...p, customer_phone: formatted }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_email.trim()) {
      toast.error('Email обязателен для оплаты');
      return;
    }
    
    const phoneDigits = formData.customer_phone.replace(/\D/g, '');
    if (phoneDigits.length < 11) {
      toast.error('Введите корректный номер телефона');
      return;
    }
    
    if (deliveryType === 'delivery' && !formData.delivery_address.trim()) {
      toast.error('Введите адрес доставки');
      return;
    }
    
    setLoading(true);
    try {
      const firstItem = cartItems[0];
      if (!firstItem) {
        toast.error('Корзина пуста');
        return;
      }
      
      const deliveryNote = deliveryType === 'pickup' 
        ? 'Самовывоз' 
        : `Доставка: ${formData.delivery_address}`;
      
      const response = await axios.post(`${API}/orders`, {
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        customer_email: formData.customer_email,
        product_id: firstItem.product.id,
        product_name: firstItem.product.name,
        product_price: total,
        quantity: 1,
        notes: `${deliveryNote}. ${formData.comment || ''}`
      });
      
      if (response.data.payment_url) {
        toast.success('Перенаправляем на оплату...');
        clearCart();
        setTimeout(() => {
          window.location.href = response.data.payment_url;
        }, 500);
      } else if (response.data.success) {
        toast.success('Заказ оформлен!');
        clearCart();
        onClose();
      } else {
        toast.error(response.data.message || 'Ошибка при оформлении заказа');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка при оформлении заказа');
    } finally { 
      setLoading(false); 
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Оформление заказа</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="checkout-form">
        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1.5">Имя *</label>
          <input 
            required 
            value={formData.customer_name} 
            onChange={(e) => setFormData(p => ({ ...p, customer_name: e.target.value }))} 
            placeholder="Ваше имя" 
            className="input-premium" 
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1.5">Телефон *</label>
          <input 
            required 
            type="tel" 
            value={formData.customer_phone}
            onChange={handlePhoneChange}
            placeholder="+7 (999) 999-99-99" 
            className="input-premium" 
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1.5">Email *</label>
          <input 
            required 
            type="email" 
            value={formData.customer_email} 
            onChange={(e) => setFormData(p => ({ ...p, customer_email: e.target.value }))} 
            placeholder="email@example.com" 
            className="input-premium" 
          />
          <p className="text-xs text-[#64748b] mt-1">Для отправки чека об оплате</p>
        </div>
        
        {/* Delivery type */}
        <div>
          <label className="block text-sm font-medium text-[#374151] mb-2">Способ получения *</label>
          <div className="grid grid-cols-2 gap-3">
            <label className={`flex items-center justify-center gap-2 p-3 border rounded-xl cursor-pointer transition-all ${deliveryType === 'pickup' ? 'border-[#14b8a6] bg-[#f0fdfa] text-[#0d9488]' : 'border-[#e2e8f0] hover:border-[#14b8a6]/50'}`}>
              <input type="radio" name="deliveryType" value="pickup" checked={deliveryType === 'pickup'} onChange={(e) => setDeliveryType(e.target.value)} className="sr-only" />
              <span className="text-sm font-medium">Самовывоз</span>
            </label>
            <label className={`flex items-center justify-center gap-2 p-3 border rounded-xl cursor-pointer transition-all ${deliveryType === 'delivery' ? 'border-[#14b8a6] bg-[#f0fdfa] text-[#0d9488]' : 'border-[#e2e8f0] hover:border-[#14b8a6]/50'}`}>
              <input type="radio" name="deliveryType" value="delivery" checked={deliveryType === 'delivery'} onChange={(e) => setDeliveryType(e.target.value)} className="sr-only" />
              <span className="text-sm font-medium">Доставка</span>
            </label>
          </div>
        </div>
        
        {deliveryType === 'pickup' && (
          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-xl p-4">
            <p className="text-sm font-medium text-[#0f172a] mb-1">Адрес самовывоза:</p>
            <p className="text-sm text-[#64748b]">г. Москва, ул. Лужники, 24, стр. 24</p>
            <p className="text-sm text-[#64748b]">Клиника доктора Косса</p>
            <p className="text-xs text-[#94a3b8] mt-2">Пн-Пт: 10:00-19:00, Сб: 11:00-17:00</p>
          </div>
        )}
        
        {deliveryType === 'delivery' && (
          <div>
            <label className="block text-sm font-medium text-[#374151] mb-1.5">Адрес доставки *</label>
            <Textarea 
              required={deliveryType === 'delivery'}
              value={formData.delivery_address} 
              onChange={(e) => setFormData(p => ({ ...p, delivery_address: e.target.value }))} 
              placeholder="Город, улица, дом, квартира" 
              rows={2} 
              className="rounded-xl border-[#e2e8f0]" 
            />
          </div>
        )}
        
        {/* Payment info */}
        <div className="bg-[#f0fdfa] border border-[#14b8a6]/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-[#0f172a]">Оплата онлайн</span>
            <span className="text-[10px] bg-[#FFDD2D] text-black px-2 py-0.5 rounded font-medium">T-Bank</span>
          </div>
          <p className="text-xs text-[#64748b]">Банковской картой или через СБП</p>
        </div>
        
        <div className="flex justify-between items-center pt-4 border-t border-[#f1f5f9]">
          <span className="text-[#64748b]">Итого:</span>
          <span className="text-xl font-bold text-[#0f172a]">{total.toLocaleString()} ₽</span>
        </div>
        <button type="submit" className="w-full btn-teal h-14" disabled={loading}>
          {loading ? 'Оформление...' : 'Перейти к оплате'}
        </button>
      </form>
    </>
  );
};

export default CheckoutForm;
